import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Save, Mail, Phone, MapPin, Shield, Crown, Award, TrendingUp, CheckCircle, Sparkles, Settings, FileText, Clock, Star, Building2, Key, Lock, Zap } from 'lucide-react';
import ImageUploader from './ImageUploader';
import PhoneInput from '../ui/PhoneInput';
import EmailInput from '../ui/EmailInput';
import CountrySelect from '../ui/CountrySelect';
import ChangePasswordForm from './ChangePasswordForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminProfileEdit({ admin, onUpdate, getAuthHeader }) {
  const [formData, setFormData] = useState({
    photo_profil_url: admin?.photo_profil_url || admin?.avatar_url || '',
    email: admin?.email || '',
    telephone: admin?.telephone || admin?.phone || '',
    nom: admin?.nom || admin?.name?.split(' ').slice(1).join(' ') || '',
    prenom: admin?.prenom || admin?.name?.split(' ')[0] || '',
    adresse: admin?.adresse || '',
    ville: admin?.ville || '',
    code_postal: admin?.code_postal || '',
    pays: admin?.pays || 'France',
    fonction: admin?.fonction || admin?.role_display || 'Super Administrateur',
    departement: admin?.departement || ''
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('identity');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUploaded = (url) => {
    setFormData(prev => ({ ...prev, photo_profil_url: url }));
    toast.success('Photo mise à jour !');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const dataToSend = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '' && v !== null)
      );

      const response = await axios.put(
        `${BACKEND_URL}/api/users/me/profile`,
        dataToSend,
        { headers: getAuthHeader() }
      );

      toast.success('Profil mis à jour avec succès !');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Calcul du pourcentage de complétion du profil
  const calculateCompletion = () => {
    const fields = ['nom', 'prenom', 'email', 'telephone', 'fonction'];
    const filled = fields.filter(f => formData[f] && formData[f].toString().trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const completion = calculateCompletion();

  // Déterminer le rôle affiché
  const getRoleDisplay = () => {
    const role = admin?.role;
    if (role === 'super_admin') return 'Super Administrateur';
    if (role === 'admin') return 'Administrateur';
    return formData.fonction || 'Administrateur';
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec carte admin et statistiques */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Carte Super Admin */}
        <Card className="lg:col-span-2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0 shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-500/10 to-purple-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          <CardContent className="pt-6 relative z-10">
            <div className="flex items-start gap-6">
              {/* Avatar avec couronne */}
              <div className="relative">
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-0.5">
                  <div className="w-full h-full rounded-2xl bg-slate-900 flex items-center justify-center overflow-hidden">
                    {formData.photo_profil_url ? (
                      <img src={formData.photo_profil_url} alt="Admin" className="w-full h-full object-cover" />
                    ) : (
                      <Crown className="h-12 w-12 text-amber-400" />
                    )}
                  </div>
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                  <Crown className="h-4 w-4 text-white" />
                </div>
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">{getRoleDisplay()}</span>
                </div>
                <h2 className="text-3xl font-bold mb-1">
                  {formData.prenom || 'Prénom'} {formData.nom || 'Nom'}
                </h2>
                <p className="text-slate-400 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {formData.email || 'email@exemple.fr'}
                </p>
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <Badge className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/30 font-semibold">
                    <Zap className="h-3 w-3 mr-1" />
                    Accès complet
                  </Badge>
                  <Badge className="bg-slate-700 text-white/50 text-slate-300 border-slate-600 font-semibold">
                    <Clock className="h-3 w-3 mr-1" />
                    {admin?.created_at ? `Depuis ${new Date(admin.created_at).getFullYear()}` : 'Membre actif'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Carte Complétion */}
        <Card className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white border-0 shadow-xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-indigo-200">Profil complété</p>
                <p className="text-4xl font-bold">{completion}%</p>
              </div>
              <div className={`p-4 rounded-2xl ${completion === 100 ? 'bg-emerald-500/30' : 'bg-white/20'}`}>
                {completion === 100 ? (
                  <Award className="h-10 w-10 text-emerald-300" />
                ) : (
                  <TrendingUp className="h-10 w-10 text-white" />
                )}
              </div>
            </div>
            <div className="w-full h-4 bg-white/20 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-700 ${
                  completion === 100 
                    ? 'bg-gradient-to-r from-emerald-400 to-teal-300' 
                    : 'bg-gradient-to-r from-white to-indigo-200'
                }`}
                style={{ width: `${completion}%` }}
              />
            </div>
            {completion === 100 ? (
              <p className="text-sm text-indigo-200 mt-3 flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-emerald-300" />
                Profil complet ! Excellent travail.
              </p>
            ) : (
              <p className="text-sm text-indigo-200 mt-3 flex items-center gap-1">
                <Sparkles className="h-4 w-4 text-amber-300" />
                Complétez votre profil
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation par sections */}
      <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-xl">
        {[
          { id: 'identity', label: 'Identité', icon: User },
          { id: 'contact', label: 'Coordonnées', icon: Phone },
          { id: 'role', label: 'Rôle & Fonction', icon: Crown },
          { id: 'security', label: 'Sécurité', icon: Lock }
        ].map(section => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all ${
              activeSection === section.id
                ? 'bg-white text-slate-900 shadow-lg'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
            }`}
          >
            <section.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{section.label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section: Identité */}
        {activeSection === 'identity' && (
          <>
            {/* Photo */}
            <Card className="border-2 border-slate-200 shadow-lg overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <User className="h-5 w-5 text-amber-400" />
                  </div>
                  Photo de profil
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Votre photo sera visible par les autres utilisateurs
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ImageUploader
                  currentImageUrl={formData.photo_profil_url}
                  onImageUploaded={handleImageUploaded}
                  type="avatar"
                  getAuthHeader={getAuthHeader}
                />
              </CardContent>
            </Card>

            {/* Informations personnelles */}
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  Informations personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <User className="h-4 w-4 text-slate-500" />
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium"
                      required
                      placeholder="Votre prénom"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                      <User className="h-4 w-4 text-slate-500" />
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base font-medium"
                      required
                      placeholder="Votre nom"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Section: Coordonnées */}
        {activeSection === 'contact' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
              <CardTitle className="flex items-center gap-3 text-lg">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Phone className="h-5 w-5 text-purple-600" />
                </div>
                Coordonnées
              </CardTitle>
              <CardDescription>
                Vos informations de contact professionnelles
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <EmailInput
                    value={formData.email}
                    onChange={(val) => setFormData(prev => ({ ...prev, email: val }))}
                    placeholder="admin@entreprise.fr"
                    required
                    label="Email professionnel"
                  />
                </div>

                <div>
                  <PhoneInput
                    value={formData.telephone}
                    onChange={(val) => setFormData(prev => ({ ...prev, telephone: val }))}
                    placeholder="Numéro de téléphone"
                    label="Téléphone"
                    showLabel={true}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    Adresse
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    placeholder="Adresse complète"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    name="code_postal"
                    value={formData.code_postal}
                    onChange={handleChange}
                    placeholder="Ex: 75001"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    Ville
                  </label>
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    placeholder="Ex: Paris"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div className="md:col-span-2">
                  <CountrySelect
                    value={formData.pays}
                    onChange={(val) => setFormData(prev => ({ ...prev, pays: val }))}
                    label="Pays"
                    showLabel={true}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Rôle & Fonction */}
        {activeSection === 'role' && (
          <Card className="border-2 border-slate-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b">
              <CardTitle className="flex items-center gap-3 text-lg">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Crown className="h-5 w-5 text-amber-600" />
                </div>
                Rôle & Fonction
              </CardTitle>
              <CardDescription>
                Vos responsabilités au sein de la plateforme
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Badge de rôle */}
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl">
                    <Crown className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Votre rôle actuel</p>
                    <p className="text-2xl font-bold">{getRoleDisplay()}</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 font-semibold">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Gestion utilisateurs
                  </Badge>
                  <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 font-semibold">
                    <Settings className="h-3 w-3 mr-1" />
                    Configuration système
                  </Badge>
                  <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 font-semibold">
                    <Key className="h-3 w-3 mr-1" />
                    Accès complet
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Star className="h-4 w-4 text-slate-500" />
                    Fonction
                  </label>
                  <input
                    type="text"
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleChange}
                    placeholder="Ex: Directeur, Responsable..."
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    Département
                  </label>
                  <input
                    type="text"
                    name="departement"
                    value={formData.departement}
                    onChange={handleChange}
                    placeholder="Ex: Direction, IT..."
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section: Sécurité */}
        {activeSection === 'security' && (
          <ChangePasswordForm 
            getAuthHeader={getAuthHeader}
            onSuccess={() => toast.success('Mot de passe modifié avec succès !')}
          />
        )}

        {/* Bouton de sauvegarde (sauf pour sécurité) */}
        {activeSection !== 'security' && (
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all text-base font-semibold"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
